import { useState, useEffect, useRef, useCallback } from 'react'
import {
  fetchGroupMessages,
  sendGroupMessage as sendGroupMessageApi,
  deleteGroupMessage as deleteGroupMessageApi,
  type CommunityMessage,
} from '@/services/communityService'

const POLL_INTERVAL = 5000
const MAX_MESSAGES = 200
const INITIAL_LIMIT = 50

export function useGroupChat(groupId: string | null, _isOpen: boolean) {
  const [messages, setMessages] = useState<CommunityMessage[]>([])
  const [sending, setLoading] = useState(false)

  const lastSeenIdRef = useRef<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isPollingRef = useRef(false)

  // ─── 初始加载 ────────────────────────────────────────

  useEffect(() => {
    if (!groupId) {
      setMessages([])
      lastSeenIdRef.current = null
      return
    }

    let cancelled = false
    const gid = groupId
    async function load() {
      try {
        const data = await fetchGroupMessages(gid, undefined, INITIAL_LIMIT)
        if (cancelled) return
        setMessages(data)
        if (data.length > 0) {
          lastSeenIdRef.current = data[data.length - 1]._id
        }
      } catch {
        // 静默失败
      }
    }
    load()
    return () => { cancelled = true }
  }, [groupId])

  // ─── 轮询逻辑 ───────────────────────────────────────

  const poll = useCallback(async () => {
    if (!groupId || isPollingRef.current) return
    isPollingRef.current = true
    try {
      const after = lastSeenIdRef.current || undefined
      const newMessages = await fetchGroupMessages(groupId, after, 50)
      if (newMessages.length === 0) return

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m._id))
        const fresh = newMessages.filter((m) => !existingIds.has(m._id))
        if (fresh.length === 0) return prev
        const merged = [...prev, ...fresh].slice(-MAX_MESSAGES)
        return merged
      })

      lastSeenIdRef.current = newMessages[newMessages.length - 1]._id
    } catch {
      // 静默
    } finally {
      isPollingRef.current = false
    }
  }, [groupId])

  useEffect(() => {
    if (!groupId) return
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL)
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [poll])

  useEffect(() => {
    if (!groupId) return
    const handleVisibility = () => {
      if (!document.hidden) poll()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [poll])

  // ─── 发送消息 ────────────────────────────────────────

  const sendMessage = useCallback(
    async (content: string, mentions: string[]) => {
      if (!groupId) return
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const optimistic: CommunityMessage = {
        _id: tempId,
        userId: '',
        username: '',
        avatar: '',
        content,
        mentions,
        groupId,
        createdAt: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, optimistic])
      setLoading(true)

      try {
        const real = await sendGroupMessageApi(groupId, content, mentions)
        setMessages((prev) =>
          prev.map((m) => (m._id === tempId ? real : m)),
        )
        lastSeenIdRef.current = real._id
      } catch (err) {
        setMessages((prev) => prev.filter((m) => m._id !== tempId))
        // 透传后端中文文案（apiClient 已把 {error} 提取为 err.message）
        throw err instanceof Error && err.message ? err : new Error('发送失败')
      } finally {
        setLoading(false)
      }
    },
    [groupId],
  )

  // ─── 删除消息 ────────────────────────────────────────

  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!groupId) return
      try {
        await deleteGroupMessageApi(groupId, messageId)
        setMessages((prev) => prev.filter((m) => m._id !== messageId))
      } catch {
        throw new Error('删除失败')
      }
    },
    [groupId],
  )

  return {
    messages,
    sending,
    sendMessage,
    deleteMessage,
  }
}
